import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { DatePickerField, Icons } from 'utoniq-ui'

import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type FilterDateInputCellProps = {
  startDate?: Date
  endDate?: Date
  onClose: () => void
  onApply: (startDate: Date | undefined, endDate: Date | undefined) => void
}

export const FilterDateInputCell: Cell<FilterDateInputCellProps> = ({
  startDate,
  endDate,
  onClose,
  onApply,
}) => {
  const { t } = useTranslation('consoleOrders')
  const [dateError, setDateError] = useState<string>('')

  const [startDateValue, setStartDateValue] = useState<dayjs.Dayjs | undefined>(
    startDate ? dayjs(startDate) : undefined
  )
  const [endDateValue, setEndDateValue] = useState<dayjs.Dayjs | undefined>(
    endDate ? dayjs(endDate) : undefined
  )

  const validateDates = useCallback(
    (start: dayjs.Dayjs | undefined, end: dayjs.Dayjs | undefined) => {
      if (start && end && dayjs(start).isAfter(dayjs(end))) {
        setDateError(
          t(
            'invalidDateRange',
            '終了日は開始日より後の日付を指定してください。'
          )
        )
        return false
      }
      if ((!start && end) || (start && !end)) {
        setDateError(
          t(
            'invalidDateInput',
            '日付を指定する場合は開始日と終了日が必須です。'
          )
        )
        return false
      }

      setDateError('')
      return true
    },
    [t]
  )

  useEffect(() => {
    validateDates(startDateValue, endDateValue)
  }, [startDateValue, endDateValue, validateDates])

  return (
    <CellFrame>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" position="relative">
              <Typography
                variant="h6"
                sx={{ width: '100%', textAlign: 'center' }}
              >
                {t('dateFilter', '日付')}
              </Typography>
              <IconButton
                onClick={onClose}
                sx={{ position: 'absolute', right: 0 }}
              >
                <Icons.Close />
              </IconButton>
            </Stack>
            <Typography variant="body1" align="center">
              {t(
                'dateFilterDescription',
                '絞り込む日付の範囲を指定してください。'
              )}
            </Typography>
            <Divider />
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" color="text.primary">
                  {t('startDate', '開始日')}
                </Typography>
                <DatePickerField
                  onChange={date => {
                    if (dayjs(date).isValid()) {
                      setStartDateValue(dayjs(date, 'YYYY/MM/DD', 'ja'))
                    } else {
                      setStartDateValue(undefined)
                    }
                  }}
                  value={startDateValue}
                  textFieldProps={{
                    inputProps: {
                      value: startDateValue?.isValid()
                        ? dayjs(startDateValue).format('YYYY/MM/DD')
                        : '',
                    },
                  }}
                  placeholder={t('startDatePlaceholder', '1990/11/11')}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" color="text.primary">
                  {t('endDate', '終了日')}
                </Typography>
                <DatePickerField
                  onChange={date => {
                    if (dayjs(date).isValid()) {
                      setEndDateValue(dayjs(date, 'YYYY/MM/DD', 'ja'))
                    } else {
                      setEndDateValue(undefined)
                    }
                  }}
                  value={endDateValue}
                  textFieldProps={{
                    inputProps: {
                      value: endDateValue?.isValid()
                        ? dayjs(endDateValue).format('YYYY/MM/DD')
                        : '',
                    },
                  }}
                  placeholder={t('endDatePlaceholder', '1990/11/11')}
                />
              </Box>
              {dateError && (
                <Typography color="error" variant="body2">
                  {dateError}
                </Typography>
              )}
            </Stack>
            <Button
              variant="contained"
              onClick={() => {
                if (!startDateValue?.isValid() && !endDateValue?.isValid()) {
                  onApply(undefined, undefined)
                } else {
                  onApply(startDateValue?.toDate(), endDateValue?.toDate())
                }
              }}
              sx={{ mt: 2 }}
              disabled={Boolean(dateError)}
            >
              {t('filter', '適用')}
            </Button>
          </Stack>
        </Box>
      </LocalizationProvider>
    </CellFrame>
  )
}
